import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, AlertCircle, MailCheck } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await apiClient.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Link to="/login" data-testid="forgot-back-to-login" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-[#475569] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft size={12} /> Back to sign in
        </Link>

        <div className="bg-white border border-[#0A0A0A]/10 p-10">
          {!submitted ? (
            <>
              <h1 className="font-display text-3xl font-black tracking-tight text-[#0A0A0A]">Forgot your password?</h1>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                Pop your email below and we&apos;ll send you a secure link to set a new one. The link is valid for 60 minutes.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="forgot-password-form">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="forgot-email-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                  />
                </div>

                {error && (
                  <div data-testid="forgot-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-submitted" className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5BB247]/10 border border-[#5BB247]/40 mb-6">
                <MailCheck size={28} className="text-[#2c8642]" />
              </div>
              <h1 className="font-display text-2xl font-black tracking-tight text-[#0A0A0A]">Check your inbox</h1>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                If an account exists for <strong>{email}</strong>, we&apos;ve sent you a reset link. It&apos;s valid for 60 minutes.
              </p>
              <p className="mt-2 text-xs text-[#475569]">
                Don&apos;t see it? Check your spam folder.
              </p>
              <Link
                to="/login"
                data-testid="forgot-return-login"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#1E4D55] hover:text-[#163C42]"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
